import { OkrKeyResultProgress } from './../../../shared/interfaces/okr-key-result-progress';
import { OkrService } from '../../../okr/okr.service';
import { OkrKeyResult } from '../../../shared/interfaces/okr-key-result.interface';
import {
  Component,
  OnInit,
  AfterViewInit,
  Inject,
  LOCALE_ID,
} from '@angular/core';
import { BaseClass } from 'src/app/shared/classes/base';
import {
  ToastController,
  ModalController,
  AlertController,
  LoadingController,
  NavParams,
} from '@ionic/angular';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UtilService } from 'src/app/shared/services/util.service';
import { formatDate } from '@angular/common';
import * as moment from 'moment';
import * as Chart from 'chart.js';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
  selector: 'app-okr-progress-model',
  templateUrl: './okr-progress-model.component.html',
  styleUrls: ['./okr-progress-model.component.scss'],
})
export class OkrProgressModelComponent extends BaseClass
  implements AfterViewInit {
  public chart: any;
  public currentProgress: number;
  public deadline: Date = new Date();
  public keyResultId: number;
  public isAdmin: boolean;
  public KeyResultComment: string;

  public keyResult: OkrKeyResult;
  private _shouldReload: boolean = false;

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    public modalController: ModalController,
    public alertController: AlertController,
    private _loadingController: LoadingController,
    private _okrService: OkrService,
    private _navParams: NavParams,
    private _utilService: UtilService,
    private _authService: AuthService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    super(_toastController, _translationService);
    this.keyResultId = this._navParams.get('keyResultId');
    this._authService.getUserRoles().forEach((element) => {
      if (element === 'Administrator' || element === 'OKRAdmin') {
        this.isAdmin = true;
      }
    });
  }

  ngAfterViewInit() {
    this._loadGoalProgress();
  }

  public dismiss() {
    this.modalController.dismiss(this._shouldReload);
  }

  public GetDateToString(date) {
    return date.toString();
  }

  public addNewProgress(): void {
    this._okrService
      .addKeyResultProgress(
        this.keyResultId,
        this.deadline,
        this.currentProgress
      )
      .subscribe(
        () => {
          this._loadGoalProgress();
          this._shouldReload = true;
          this.notify('Progresso adicionado com sucesso', 'success');
        },
        (error) => this.notify(this.getErrorNotification(error), 'danger')
      );
  }

  private _getDateFromValue(value: number): string {
    const progress = this.keyResult.progresses.find(
      (p) => p.progress === value
    );
    return progress
      ? formatDate(progress.progressDate, 'dd/MM/yyyy', this.locale) + ' - '
      : '';
  }

  public getDateDiff(KeyResultProgress: OkrKeyResultProgress): number {
    const momentDueDate = moment(KeyResultProgress.progressDate).startOf('day');
    const momentToday = moment().startOf('day');
    return moment.duration(momentToday.diff(momentDueDate)).asDays();
  }

  private async _loadGoalProgress(): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    this._okrService.getKeyResult(this.keyResultId).subscribe(
      (response) => {
        this.keyResult = response.data;
        this.KeyResultComment = this.keyResult.comment;
        this._createProgressChar(this.keyResult);
        loading.dismiss();
      },
      (error) => {
        loading.dismiss();
        this.notify(this.getErrorNotification(error), 'danger');
      }
    );
  }

  private async saveKeyResultComment(): Promise<void> {
    if (
      this.KeyResultComment === this.keyResult.comment
    ) {
      this.notify('OKR.commentNotAlteredOrEmpty', 'danger');
      return;
    }

    const loading = await this._loadingController.create();
    await loading.present();

    this._okrService
      .updateKeyResultComment(this.keyResultId, this.KeyResultComment)
      .subscribe(
        (response) => {
          this.keyResult.comment = this.KeyResultComment;
          loading.dismiss();
          this.notifySuccess();
        },
        (error) => {
          loading.dismiss();
          this.notify(this.getErrorNotification(error), 'danger');
        }
      );
  }

  private _createProgressChar(keyResult: OkrKeyResult): void {
    const DeadlineDate = new Date(keyResult.deadline);

    const afterDeadlineCount = keyResult.progresses.reduce((sum, progress) => {
      progress.progressDate = new Date(progress.progressDate);
      return progress.progressDate > DeadlineDate ? sum + 1 : sum;
    }, 0);

    const canvas = document.getElementById('canvasContainer') as any;
    this._registerCharPlugin(afterDeadlineCount, canvas.offsetWidth);

    const hasProgressInDeadline = keyResult.progresses.some((progress) => {
      const ProgressDate = new Date(progress.progressDate);
      ProgressDate.setHours(0, 0, 0, 0);
      DeadlineDate.setHours(0, 0, 0, 0);
      return ProgressDate === DeadlineDate;
    });

    let latestAfterDeadline: OkrKeyResultProgress | null = null;

    if (afterDeadlineCount > 0 && !hasProgressInDeadline) {
      const beforeDeadline = keyResult.progresses.filter((progress) => {
        const ProgressDate = new Date(progress.progressDate);
        return ProgressDate < DeadlineDate;
      });

      const maxProgress = Math.max.apply(
        Math,
        beforeDeadline.map((p) => p.progress)
      );

      keyResult.progresses.push({
        progressDate: DeadlineDate,
        progress: maxProgress,
      });

      latestAfterDeadline = keyResult.progresses.reduce((progress, current) => {
        const ProgressDate = new Date(progress.progressDate);
        return ProgressDate > DeadlineDate &&
          progress.progressDate > current.progressDate
          ? progress
          : current;
      });
    }

    const data: Array<OkrKeyResultProgress> = this._utilService.orderByProperty(
      keyResult.progresses,
      'progressDate'
    );

    const adjustesData = data.map((p) => {
      return {
        x: moment(p.progressDate).format('DD/MM/YYYY'),
        y: p.progress,
      };
    });

    adjustesData.splice(0, 0, {
      x: moment(keyResult.createdAt).format('DD/MM/YYYY'),
      y: 0,
    });

    const ctx2 = document.getElementById('myChart') as HTMLCanvasElement;
    this.chart = new Chart(ctx2, <any>{
      type: 'line',
      data: {
        datasets: [
          {
            label: '',
            data: [...adjustesData],
            backgroundColor: 'transparent',
            borderColor: '#FFFFFF',
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: '#FFFFFF',
            pointHoverBackgroundColor: '#001e62',
            pointHoverBorderColor: '#001e62',
            borderWidth: 6,
            pointRadius: 8,
            pointHoverRadius: 10,
          },
        ],
      },
      options: {
        legend: { display: false },
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        bezierCurve: true,
        tooltips: {
          enabled: true,
          callbacks: {
            label: ((tooltipItem, graphData) => {
              const value =
                graphData.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ];
              return value.x + ' - ' + value.y + '%';
            }).bind(this),
          },
        },
        chartArea: {
          filledBackgroundColor: '#195ab4',
          missingBackgroundColor: '#d2224b',
        },
        scales: {
          yAxes: [
            {
              gridLines: { display: false },
              ticks: {
                fontColor: '#001e62',
                fontSize: 11,
                fontStyle: 'bold',
                stepSize: 25,
                beginAtZero: true,
                min: 0,
                max: 100,
              },
            },
            {
              display: afterDeadlineCount === 0,
              gridLines: { display: false },
              position: 'right',
              scaleLabel: {
                display: true,
                position: 'right',
                labelString: 'deadline',
                backgroundColor: '#d2224b',
                backdropColor: '#d2224b',
                fontColor: 'white',
                fontStyle: 'bold',
              },
              ticks: { display: false },
            },
          ],
          xAxes: [
            {
              type: 'time',
              time: {
                displayFormats: {
                  month: 'MM/YYYY',
                },
                format: 'DD/MM/YYYY',
                tooltipFormat: 'll',
                min: moment(keyResult.createdAt).format('DD/MM/YYYY'),
                max: moment(
                  latestAfterDeadline
                    ? latestAfterDeadline.progressDate
                    : keyResult.deadline
                ).format('DD/MM/YYYY'),
              },
              ticks: {
                fontColor: '#001e62',
                fontSize: 11,
                fontStyle: 'bold',
                display: true,
              },
              gridLines: { display: false },
            },
          ],
        },
      },
    });
  }

  private _registerCharPlugin(
    afterDeadline: number,
    canvasWidth: number = 600
  ): void {
    Chart.pluginService.register({
      beforeDraw: function (c, easing) {
        const chart = c as any;
        if (
          chart.config.options.chartArea &&
          chart.config.options.chartArea.missingBackgroundColor
        ) {
          const ctx = chart.chart.ctx;
          const chartArea = chart.chartArea;

          const meta = chart.getDatasetMeta(0);

          const dataStartIndex = meta.data[0]._model.x;
          const startIndex = meta.data.length - (afterDeadline + 1);
          const start = meta.data[startIndex]._model.x;
          const stop = canvasWidth;

          ctx.save();

          ctx.fillStyle = chart.config.options.chartArea.filledBackgroundColor;

          if (afterDeadline > 0) {
            ctx.fillRect(
              dataStartIndex,
              chartArea.top,
              start - dataStartIndex,
              chartArea.bottom - chartArea.top
            );
            ctx.fillStyle =
              chart.config.options.chartArea.missingBackgroundColor;
            ctx.fillRect(
              start,
              chartArea.top,
              stop - start,
              chartArea.bottom - chartArea.top
            );
          } else {
            ctx.fillRect(
              dataStartIndex,
              chartArea.top,
              stop,
              chartArea.bottom - chartArea.top
            );
          }

          ctx.restore();
        }
      },
    });
  }
}
